import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { generateQuotePdf } from "@/lib/quotePdf";
import { Plus, FilePdf, Trash, Receipt } from "@phosphor-icons/react";

const INR = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });

export default function Quotes() {
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/quotes");
      setQuotes(data);
    } catch {
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const downloadPdf = async (q) => {
    try {
      const { data } = await api.get(`/quotes/${q.id}`);
      await generateQuotePdf(data);
      toast.success("PDF downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  const removeQuote = async (q) => {
    if (!window.confirm(`Delete quote ${q.quote_number}?`)) return;
    try {
      await api.delete(`/quotes/${q.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="min-h-screen bg-canvas" data-testid="quotes-page">
      <Header />
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 animate-fade-up">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-brand mb-3 font-bold flex items-center gap-2">
              <Receipt size={16} weight="bold" /> Quotes
            </div>
            <h1 className="font-display font-black text-zinc-900 text-4xl sm:text-5xl leading-[0.95] tracking-tight">
              Every quote. <span className="text-brand italic">One list.</span>
            </h1>
            <p className="text-zinc-500 mt-3 max-w-2xl">
              A running history of every quotation your team has generated. Re-download PDFs anytime.
            </p>
          </div>
          <Button
            onClick={() => nav("/quotes/new")}
            data-testid="new-quote-btn"
            className="bg-brand hover:bg-brand-hover text-white gap-2 h-11 shadow-sm"
          >
            <Plus size={16} weight="bold" /> New Quote
          </Button>
        </div>

        <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden shadow-sm">
          <div className="px-5 py-3 border-b border-zinc-200 bg-cream text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold flex items-center justify-between">
            <span>{loading ? "Loading..." : `${quotes.length} quotes`}</span>
          </div>
          <div className="overflow-auto max-h-[calc(100vh-22rem)]">
            <table className="pricelist w-full" data-testid="quotes-table">
              <thead>
                <tr>
                  <th>Quote #</th>
                  <th>Client</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Valid Until</th>
                  <th>Created By</th>
                  <th>Created</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {!loading && quotes.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center text-zinc-400 py-16">
                      <Receipt size={40} className="mx-auto mb-3 text-zinc-300" />
                      No quotes yet. Click <span className="font-semibold text-brand">New Quote</span> to start.
                    </td>
                  </tr>
                )}
                {quotes.map((q) => (
                  <tr key={q.id} data-testid={`quote-row-${q.id}`}>
                    <td className="font-mono-num text-brand font-bold">{q.quote_number}</td>
                    <td>
                      <div className="text-zinc-900 font-semibold">{q.client?.name}</div>
                      {q.client?.company && <div className="text-xs text-zinc-500">{q.client.company}</div>}
                    </td>
                    <td className="text-zinc-700">{q.line_items?.length || 0}</td>
                    <td className="font-mono-num text-zinc-900 font-bold">{INR.format(q.total || 0)}</td>
                    <td className="text-zinc-600 text-sm">{q.valid_until ? new Date(q.valid_until).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</td>
                    <td className="text-zinc-600 text-xs">{q.created_by_email}</td>
                    <td className="text-zinc-500 text-xs">{new Date(q.created_at).toLocaleString("en-IN")}</td>
                    <td className="text-right whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => downloadPdf(q)}
                        data-testid={`pdf-${q.id}`}
                        className="text-zinc-700 hover:text-brand hover:bg-brand-soft gap-1"
                      >
                        <FilePdf size={14} weight="bold" /> PDF
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeQuote(q)}
                        data-testid={`del-quote-${q.id}`}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1"
                      >
                        <Trash size={14} /> Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
